/**
 * 購物車頁的商品備註：每件商品各自存成明細屬性「商品備註」（訂單頁看得到）。
 *
 * 另外把整台購物車的備註彙整成一筆訂單屬性「商品備註彙整」（2026-10-07）。
 * 原因：Shopify 後台內建的訂單匯出 CSV 欄位固定，不含商品明細屬性，但會匯出
 * 訂單屬性（`Note Attributes` 欄），所以多抄一份到訂單層級，匯出時就看得到客製化文字。
 * 同步時機：購物車頁載入時、每次存完備註後、按下結帳前。彙整失敗不擋結帳（每件商品的備註仍在）。
 * 除了用 Ajax 寫入購物車，也同步到表單的隱藏欄位 [data-cart-note-summary]：按「前往結帳」時表單會帶著
 * 發票等屬性送出，不在表單裡的屬性會遺失（2026-10-07 實測），所以兩邊都要有。
 *
 * ⚠️ 彙整一律從「購物車頁上的資料」算（每列的商品名、款式、數量欄位、備註），**不要改成讀 /cart.js**：
 * /cart.js 前面有 Shopify 的快取，會回舊的購物車（見 features/README.md），2026-10-07 實測它回了
 * 還沒有備註的舊資料，算出空的彙整把已寫好的彙整蓋掉。頁面是伺服器即時渲染，結帳送出也以表單為準。
 */
(() => {
  const cartForm = document.querySelector('[data-cart-line-notes]');
  if (!cartForm) return;

  const notePropertyName = '商品備註';
  const summaryAttributeName = '商品備註彙整';
  const cartChangeUrl = `${cartForm.dataset.cartChangeUrl}.js`;
  const cartUpdateUrl = `${cartForm.dataset.cartUpdateUrl}.js`;
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };
  /** 表單內的隱藏欄位：結帳送出表單時會以表單上的屬性為準，彙整必須在表單裡才會帶進訂單 */
  const summaryInput = cartForm.querySelector('[data-cart-note-summary]');
  /** 目前購物車上的彙整內容，初始值是伺服器渲染時的屬性值 */
  let savedSummary = null;
  const rows = Array.from(cartForm.querySelectorAll('[data-cart-line-row]'));
  const textareas = Array.from(cartForm.querySelectorAll('[data-cart-line-note]'));
  const timers = new WeakMap();
  let saveQueue = Promise.resolve();
  let submittingAfterSave = false;

  const messages = {
    saving: cartForm.dataset.noteSaving,
    saved: cartForm.dataset.noteSaved,
    error: cartForm.dataset.noteError,
    checkoutError: cartForm.dataset.noteCheckoutError,
  };

  function setStatus(textarea, message, state = '') {
    const status = textarea.closest('[data-cart-line-row]')?.querySelector('[data-cart-line-note-status]');
    if (!status) return;
    status.textContent = message;
    status.dataset.state = state;
  }

  function getProperties(row) {
    const data = row.querySelector('[data-cart-line-properties]');
    if (!data) return {};

    try {
      const properties = JSON.parse(data.textContent);
      if (Array.isArray(properties)) return Object.fromEntries(properties);
      return properties && typeof properties === 'object' ? properties : {};
    } catch (error) {
      console.error('Unable to read cart line properties.', error);
      return {};
    }
  }

  function syncRows(cart) {
    if (!cart?.items || cart.items.length !== rows.length) {
      window.location.reload();
      return;
    }

    rows.forEach((row, index) => {
      const cartItem = cart.items[index];
      const propertiesData = row.querySelector('[data-cart-line-properties]');
      row.dataset.lineKey = cartItem.key;
      if (propertiesData) propertiesData.textContent = JSON.stringify(cartItem.properties || {});
    });
  }

  function saveNote(textarea) {
    window.clearTimeout(timers.get(textarea));

    const value = textarea.value;
    if (value === textarea.dataset.savedValue) return Promise.resolve();

    const row = textarea.closest('[data-cart-line-row]');
    if (!row) return Promise.resolve();

    setStatus(textarea, messages.saving, 'saving');

    const task = async () => {
      const properties = getProperties(row);
      properties[notePropertyName] = value;

      const response = await fetch(cartChangeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: row.dataset.lineKey,
          properties,
        }),
      });

      if (!response.ok) throw new Error(`Cart note update failed with status ${response.status}`);

      const cart = await response.json();
      textarea.dataset.savedValue = value;
      syncRows(cart);
      setStatus(textarea, messages.saved, 'saved');
      // 彙整失敗只記錄、不影響這件商品的備註已存好
      await syncNoteSummary().catch((error) => console.error(error));
    };

    const result = saveQueue.then(task, task);
    saveQueue = result.catch(() => undefined);

    return result.catch((error) => {
      console.error(error);
      setStatus(textarea, messages.error, 'error');
      throw error;
    });
  }

  /**
   * 依購物車頁上的資料，把每件有備註的商品彙整成一行：「商品名（款式）×數量：備註 ｜ …」。
   * 數量取表單上的數量欄位（結帳送出時也以它為準），備註取備註欄位；換行改成空白，讓匯出的 CSV 一格一行。
   * @returns {string} 彙整文字；沒有任何備註時為空字串
   */
  function buildNoteSummary() {
    return rows
      .map((row) => {
        const note = String(row.querySelector('[data-cart-line-note]')?.value || '').replace(/\s*\n\s*/g, ' ').trim();
        if (!note) return '';
        const quantity = parseInt(row.querySelector('input[name="updates[]"]')?.value, 10) || 1;
        const variant = row.dataset.lineVariant ? `（${row.dataset.lineVariant}）` : '';
        return `${row.dataset.lineTitle || ''}${variant} ×${quantity}：${note}`;
      })
      .filter(Boolean)
      .join(' ｜ ');
  }

  /**
   * 更新表單隱藏欄位；內容有變才用 Ajax 寫回購物車的訂單屬性（只更新這一個屬性，不動發票、物流等其他屬性）。
   * @returns {Promise<void>}
   */
  async function syncNoteSummary() {
    if (savedSummary === null) savedSummary = summaryInput?.defaultValue || '';
    const summary = buildNoteSummary();
    if (summaryInput) summaryInput.value = summary;
    if (summary === savedSummary) return;

    const response = await fetch(cartUpdateUrl, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ attributes: { [summaryAttributeName]: summary } }),
    });
    if (!response.ok) throw new Error(`Cart note summary update failed with status ${response.status}`);
    savedSummary = summary;
  }

  /**
   * 把彙整同步排進與備註儲存相同的佇列，避免兩邊的請求交錯。
   * @returns {Promise<void>}
   */
  function queueSummarySync() {
    const result = saveQueue.then(syncNoteSummary, syncNoteSummary);
    saveQueue = result.catch(() => undefined);
    return result;
  }

  function scheduleSave(textarea) {
    window.clearTimeout(timers.get(textarea));
    setStatus(textarea, '', '');
    timers.set(textarea, window.setTimeout(() => {
      saveNote(textarea).catch(() => undefined);
    }, 800));
  }

  // 載入時同步一次：數量改了、商品刪了之後回到購物車頁，彙整會跟著更新
  queueSummarySync().catch((error) => console.error(error));

  textareas.forEach((textarea) => {
    textarea.addEventListener('input', () => scheduleSave(textarea));
    textarea.addEventListener('blur', () => saveNote(textarea).catch(() => undefined));
  });

  cartForm.addEventListener('submit', async (event) => {
    if (submittingAfterSave) return;

    const dirtyTextareas = textareas.filter((textarea) => textarea.value !== textarea.dataset.savedValue);
    const isCheckout = event.submitter?.name === 'checkout';
    if (dirtyTextareas.length === 0 && !isCheckout) return;

    event.preventDefault();
    const submitter = event.submitter;
    const submitButtons = cartForm.querySelectorAll('button[type="submit"]');
    submitButtons.forEach((button) => { button.disabled = true; });

    const results = await Promise.allSettled(dirtyTextareas.map(saveNote));
    const failed = results.some((result) => result.status === 'rejected');
    // 結帳前確保彙整是最新的；失敗不擋結帳（每件商品的備註仍會帶進訂單）
    if (!failed && isCheckout) await queueSummarySync().catch((error) => console.error(error));

    if (!failed) {
      submittingAfterSave = true;
      // 先恢復按鈕再送出：Chrome 對「被停用的按鈕」呼叫 requestSubmit 會直接忽略、不送出（2026-10-07 實測）。
      // 以前只有備註要先存時才會攔截，存檔期間 Waaship 剛好會把按鈕重新啟用，所以沒被發現。
      submitButtons.forEach((button) => { button.disabled = false; });
      // 排到下一個工作週期再送出：若前面的等待都已完成（例如備註早就存好），這段程式會在原本那次
      // submit 事件還沒派發完時就執行，而表單在派發 submit 事件期間會忽略再次送出（HTML 規範），
      // 結果按了結帳卻沒反應（2026-10-07 實測）。
      window.setTimeout(() => cartForm.requestSubmit(submitter), 0);
    } else {
      window.alert(messages.checkoutError);
      submitButtons.forEach((button) => { button.disabled = false; });
    }
  });
})();
